import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CaretDown, 
  Check, 
  SignOut, 
  Buildings,
  Warning,
  Plus,
  X
} from '@phosphor-icons/react';
import { 
  getAccessibleAccounts, 
  setScopedAccount, 
  clearScopedAccount,
  getEffectiveAccountId,
  getEffectiveAccountName,
  isAdmin,
  isScoped
} from '../services/authorizationService';
import { createAccount } from '../services/adminService';

export default function AccountSwitcher() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isExiting, setIsExiting] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newAccountName, setNewAccountName] = useState('');
  const [newAccountDescription, setNewAccountDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Load accounts on component mount
  useEffect(() => {
    if (isAdmin()) {
      loadAccounts();
    }
  }, []);

  const loadAccounts = async () => {
    setIsLoading(true);
    try {
      const accountsList = await getAccessibleAccounts();
      setAccounts(accountsList);
    } catch (error) {
      console.error('Error loading accounts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccountSelect = async (account) => {
    try {
      await setScopedAccount(account.id, account.name);
      setIsOpen(false);
      setSearchQuery('');
      
      // Navigate to Overview page when switching to an account
      navigate('/dashboard');
    } catch (error) {
      console.error('Error selecting account:', error);
    }
  };

  const handleExitScope = async () => {
    setIsExiting(true);
    try {
      await clearScopedAccount();
      setIsOpen(false);
      
      // Navigate to Users page when exiting account scope (admin view)
      navigate('/dashboard/users');
    } catch (error) {
      console.error('Error exiting scope:', error);
    } finally {
      setIsExiting(false);
    }
  };

  const handleCreateAccount = async () => {
    if (!newAccountName.trim()) {
      setMessage({ type: 'error', text: 'Account name is required' });
      return;
    }

    setIsCreating(true);
    try {
      const accountId = await createAccount(newAccountName.trim(), newAccountDescription.trim());
      
      // Refresh accounts list
      await loadAccounts();
      
      // Clear form
      setNewAccountName('');
      setNewAccountDescription('');
      setShowCreateForm(false);
      
      setMessage({ type: 'success', text: `Successfully created account: ${newAccountName}` });
      
      // Auto-select the new account
      const newAccount = { id: accountId, name: newAccountName.trim() };
      await handleAccountSelect(newAccount);
      
    } catch (error) {
      console.error('Error creating account:', error);
      setMessage({ type: 'error', text: `Failed to create account: ${error.message}` });
    } finally {
      setIsCreating(false);
    }
  };

  // const showMessage = (type, text) => {
  //   setMessage({ type, text });
  //   setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  // };

  const filteredAccounts = accounts.filter(account =>
    account.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const currentAccountId = getEffectiveAccountId();
  const currentAccountName = getEffectiveAccountName();

  if (!isAdmin()) {
    return null;
  }

  return (
    <div className="relative">
      {/* Account Switcher Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <Buildings className="w-4 h-4 text-gray-600" />
        <span className="text-sm font-medium text-gray-700">
          {isScoped() ? (
            <>
              <span className="text-blue-600">Operating as:</span>
              <span className="ml-1">{currentAccountName}</span>
            </>
          ) : (
            'Select Account'
          )}
        </span>
        <CaretDown className="w-4 h-4 text-gray-500" />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Switch Account</h3>
                <p className="text-xs text-gray-500 mt-1">
                  Select a client account to operate within
                </p>
              </div>
              <button
                onClick={() => setShowCreateForm(!showCreateForm)}
                className="flex items-center space-x-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              >
                <Plus className="w-3 h-3" />
                <span>New Client</span>
              </button>
            </div>
          </div>

          {/* Message */}
          {message.text && (
            <div className={`px-4 py-2 text-xs ${
              message.type === 'success' ? 'bg-green-100 text-green-800' :
              message.type === 'error' ? 'bg-red-100 text-red-800' :
              'bg-blue-100 text-blue-800'
            }`}>
              {message.text}
            </div>
          )}

          {/* Create Account Form */}
          {showCreateForm && (
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Account Name *
                  </label>
                  <input
                    type="text"
                    value={newAccountName}
                    onChange={(e) => setNewAccountName(e.target.value)}
                    placeholder="Enter client name"
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Description (Optional)
                  </label>
                  <input
                    type="text"
                    value={newAccountDescription}
                    onChange={(e) => setNewAccountDescription(e.target.value)}
                    placeholder="Brief description"
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={handleCreateAccount}
                    disabled={isCreating || !newAccountName.trim()}
                    className="flex-1 flex items-center justify-center space-x-1 px-2 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-3 h-3" />
                    <span>{isCreating ? 'Creating...' : 'Create & Select'}</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewAccountName('');
                      setNewAccountDescription('');
                    }}
                    className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Search */}
          {!showCreateForm && (
            <div className="px-4 py-3 border-b border-gray-200">
              <input
                type="text"
                placeholder="Search accounts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {/* Account List */}
          {!showCreateForm && (
            <div className="max-h-64 overflow-y-auto">
              {isLoading ? (
                <div className="px-4 py-8 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">Loading accounts...</p>
                </div>
              ) : filteredAccounts.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <Warning className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">
                    {searchQuery ? 'No accounts found' : 'No accounts available'}
                  </p>
                </div>
              ) : (
                filteredAccounts.map((account) => (
                  <button
                    key={account.id}
                    onClick={() => handleAccountSelect(account)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center justify-between group"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <Buildings className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{account.name}</p>
                        <p className="text-xs text-gray-500">Account ID: {account.id}</p>
                      </div>
                    </div>
                    {account.id === currentAccountId && (
                      <Check className="w-4 h-4 text-blue-600" />
                    )}
                  </button>
                ))
              )}
            </div>
          )}

          {/* Footer Actions */}
          {!showCreateForm && isScoped() && (
            <div className="px-4 py-3 border-t border-gray-200">
              <button
                onClick={handleExitScope}
                disabled={isExiting}
                className="w-full flex items-center justify-center space-x-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
              >
                <SignOut className="w-4 h-4" />
                <span>{isExiting ? 'Exiting...' : 'Exit Account Scope'}</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}
